import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import FolderSelector from 'file-browser/FolderSelector';
import {showBasicHelpModal} from 'help/Modals';
import {Party} from 'parties/PartyConstants';
import EntityStore from 'stores/EntityStore';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {EntityInputWithSearch} from 'ui-library/entity-input/EntityInputWithSearch';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import * as modals from 'util/Modals';
import {ElementMouseEvent, InputChangeEvent, TextAreaChangeEvent} from 'util/Types';
import * as WorkflowActions from 'workflows/WorkflowActions';
import {Workflow} from 'workflows/WorkflowConstants';

function showWorkflowAuthorsHelp(e: ElementMouseEvent): void {
  showBasicHelpModal(
    e,
    'Workflow Authors',
    <div>
      Workflow authors only affect the workflow itself, and do not affect the entries inside the workflow.
    </div>
  );
}

interface WorkflowSettingsModalProps {
  onRequestClose: () => void;
  workflow: Workflow;
}

interface State {
  description: string | null;
  rootFolderId: string;
  nameInputValue: string;
  authors: Array<Party>;
}

class WorkflowSettingsModal extends React.Component<WorkflowSettingsModalProps, State> {
  override state: State = {
    description: this.props.workflow.description,
    rootFolderId: this._getRootFolderId(),
    nameInputValue: this.props.workflow.name,
    authors: this.props.workflow.authors,
  };

  _getRootFolderId(): string {
    return this.props.workflow.folder
      ? this.props.workflow.folder.api_identifier
      : this.props.workflow.source.rootFolder.api_identifier;
  }

  _getAuthorTags(): Array<{id: string; handle: string; text: string}> {
    return this.state.authors.map(({handle, id}) => ({id, handle, text: handle}));
  }

  _handleSetNameInputValue = ({target: {value}}: InputChangeEvent): void => {
    this.setState({nameInputValue: value});
  };

  _handleChangeProject = ({target: {value}}: {target: {value: string}}): void => {
    this.setState({rootFolderId: value});
  };

  _handleChangeDescription = ({target: {value}}: TextAreaChangeEvent): void => {
    this.setState({description: value});
  };

  _handleChangeAuthors = (authors: Array<Party>): void => {
    this.setState({authors});
  };

  _handleDeleteWorkflow = (): void => {
    let title;
    let bodyComponent;
    const deleteMessage = 'Are you sure you want to delete this workflow? This action cannot be undone.';
    if (this.props.workflow.initializationDatas.length > 0) {
      title = 'Confirm delete workflow that fulfills requests';
      bodyComponent = (
        <span>
          <strong>This workflow fulfills requests.</strong> {deleteMessage}
        </span>
      );
    } else {
      title = 'Confirm delete workflow';
      bodyComponent = <span>{deleteMessage}</span>;
    }

    modals.confirm(title, bodyComponent, 'danger').done(() => {
      WorkflowActions.deleteWorkflow(this.props.workflow.id);
      this.props.onRequestClose();
    });
  };

  _handleRequestSubmit = (): void => {
    const newData: {
      name?: string;
      description?: string;
      rootFolderId?: string;
      authorIds?: Array<string>;
    } = {};
    if (this.props.workflow.name !== this.state.nameInputValue) {
      newData.name = this.state.nameInputValue;
    }
    if (this.state.description != null && this.props.workflow.description !== this.state.description) {
      newData.description = this.state.description;
    }
    if (this._getRootFolderId() !== this.state.rootFolderId) {
      newData.rootFolderId = this.state.rootFolderId;
    }
    // eslint-disable-next-line no-restricted-syntax
    if (!isEqual(_.pluck(this.props.workflow.authors, 'id'), _.pluck(this.state.authors, 'id'))) {
      // eslint-disable-next-line no-restricted-syntax
      newData.authorIds = _.pluck(this.state.authors, 'id');
    }
    WorkflowActions.editWorkflowMetadata(this.props.workflow.id, newData);
    this.props.onRequestClose();
  };

  _getButtons(): Array<ModalButton> {
    const buttons: Array<ModalButton> = [
      {
        text: 'Save',
        style: ButtonStyle.PRIMARY,
        disabled: this.state.nameInputValue.length === 0,
        submit: true,
        key: 'save',
      },
    ];
    if (BenchlingConfig.LEGACY_WORKFLOWS_ALLOW_DELETION) {
      buttons.push({
        text: `Delete workflow`,
        style: ButtonStyle.NEGATIVE,
        pullLeft: true,
        onClick: this._handleDeleteWorkflow,
        key: 'delete',
      });
    }
    return buttons;
  }

  override render(): JSX.Element {
    return (
      <Modal
        title={`Manage ${this.props.workflow.humanId}`}
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this._handleRequestSubmit}
        buttons={this._getButtons()}
      >
        <Row>
          <Col xs={6}>
            <TextField
              autoFocus={true}
              label="Name"
              labelStyle={LABEL_STYLES.LABEL}
              value={this.state.nameInputValue}
              onChange={this._handleSetNameInputValue}
            />
          </Col>
          {this.props.workflow.permissions.writable && (
            <Col xs={6}>
              <FieldLabel label="Folder" />
              <FolderSelector
                noFolderText="Choose folder"
                value={this.state.rootFolderId}
                onChange={this._handleChangeProject}
                /* Only render notebook folders */
                showInventory={false}
                showNotebook={true}
                showRegistries={false}
                unsetOnFetchFail={true}
              />
            </Col>
          )}
        </Row>
        <Row>
          <Col xs={12}>
            <FieldLabel label="Authors" />
            <a onClick={showWorkflowAuthorsHelp} className="u-tiny-spacing-left">
              <BenchlingIcon icon="circle-question" />
            </a>
            <EntityInputWithSearch
              allowAdjustInputWidth={false}
              allowTeams={false}
              allowOrganizations={false}
              onChange={this._handleChangeAuthors}
              organizationIds={EntityStore.currentUser().organizations.map(({id}: {id: string}) => id)}
              size={TagsInputSize.SMALL}
              value={this._getAuthorTags()}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <FieldLabel label="Description" />
            <textarea
              className="workflowSettingsModal-description"
              value={this.state.description || ''}
              onChange={this._handleChangeDescription}
              rows={3}
            />
          </Col>
        </Row>
      </Modal>
    );
  }
}

export default WorkflowSettingsModal;
